import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { api } from "../../api/client";
import { qk } from "../../api/queries";
import type { TaskDetail, TimelineEvent } from "../../api/types";
import { str } from "../../lib/format";
import { skipChoice } from "../../lib/skip";
import { GATE_LABEL } from "../../lib/status";
import { AttachmentList } from "../Attachments";
import { Composer, SubmitCancelled } from "../Composer";
import { useDialogs } from "../Dialogs";
import { DiffView } from "../DiffView";
import { Icon } from "../Icon";
import { Markdown } from "../Markdown";
import { Pill } from "../Pill";
import { RelativeTime } from "../RelativeTime";
import { ErrorBox, Spinner } from "../Spinner";
import { LivePreviewCard } from "./LivePreview";

/** Outputs of the current round: the latest stage_completed and later gate results. */
function currentRound(timeline: TimelineEvent[] | undefined) {
  let lastCompleted = -1;
  timeline?.forEach((e, i) => {
    if (e.kind === "stage_completed") lastCompleted = i;
  });
  const completed = lastCompleted >= 0 ? timeline![lastCompleted] : undefined;
  const findings = (timeline ?? []).filter((e, i) => e.kind === "gate_result" && i > lastCompleted);
  return { completed, findings };
}

/** Shown when status is human_review: everything a reviewer needs. The decision is made in the Activity thread (ReviewDecision). */
export function ReviewPanel({ task, timeline }: { task: TaskDetail; timeline: TimelineEvent[] | undefined }) {
  const stageIndex = task.stages.findIndex((s) => s.key === task.stage_key);
  const { completed, findings } = currentRound(timeline);
  const running = task.parallel_reviews ?? [];

  return (
    <section className="review-panel">
      <header className="review-header">
        <Icon name="gate" />
        <div>
          <div className="review-title">
            Review requested{task.current_gate ? `: ${task.current_gate.name || GATE_LABEL[task.current_gate.type]}` : ""}
          </div>
          <div className="muted small">
            {task.stages[stageIndex]?.name ?? task.stage_key}
            {task.gate_index !== null && ` · gate ${task.gate_index + 1} of ${task.stages[stageIndex]?.gates.length ?? "?"}`}
          </div>
        </div>
      </header>

      <LivePreviewCard task={task} />

      {completed && (
        <div className="review-block">
          <h4>Stage output</h4>
          {str(completed.data.summary) && <Markdown>{str(completed.data.summary)}</Markdown>}
          {completed.body && completed.body !== completed.data.summary && <Markdown>{completed.body}</Markdown>}
          {completed.attachments.length > 0 && <AttachmentList attachments={completed.attachments} />}
          {str(completed.data.diff_stat) && <pre className="diff-stat">{str(completed.data.diff_stat)}</pre>}
        </div>
      )}

      {running.length > 0 && (
        <div className="review-block parallel-review">
          <Spinner />
          <span>
            <strong>{running.map((r) => r.gate?.name ?? "AI review").join(", ")}</strong> {running.length === 1 ? "is" : "are"} running
            alongside your review
            {running[0].started_at ? (
              <span className="muted">
                {" "}
                · started <RelativeTime iso={running[0].started_at} />
              </span>
            ) : (
              <span className="muted"> · waiting for capacity</span>
            )}
            . Findings appear here when ready.
          </span>
        </div>
      )}

      {findings.length > 0 && (
        <ReviewToggle title="AI review findings" summary={<VerdictSummary findings={findings} />}>
          {findings.map((f) => {
            const verdict = str(f.data.verdict);
            const gate = f.data.gate as { name?: string; type?: string } | string | undefined;
            const gateName = typeof gate === "string" ? gate : gate?.name ?? gate?.type ?? "Review";
            return (
              <div key={f.id} className="finding">
                <div className="finding-header">
                  <strong>{gateName}</strong>
                  {verdict && <Pill color={verdict === "pass" ? "green" : "red"}>{verdict}</Pill>}
                  <span className="muted small">
                    {f.actor.name} · <RelativeTime iso={f.created_at} />
                  </span>
                </div>
                {f.body ? <Markdown>{f.body}</Markdown> : <p className="muted">No findings.</p>}
              </div>
            );
          })}
        </ReviewToggle>
      )}

      <div className="review-block review-richdiff">
        <div>
          <strong>Rich diff</strong>
          <span className="muted small"> · traceability (requirements, tests, risks, security risks) and database schema changes</span>
        </div>
        <OpenRichDiff projectId={task.project_id} taskId={task.id} />
      </div>

      <ReviewToggle title="Code changes" summary={<span>{diffSummary(str(completed?.data.diff_stat))}</span>}>
        <TaskDiffSection taskId={task.id} />
      </ReviewToggle>

    </section>
  );
}

/**
 * Approve / Reject (or just comment) while a task is in human review: replaces
 * the comment box at the top of the Activity thread.
 */
export function ReviewDecision({ task, timeline }: { task: TaskDetail; timeline: TimelineEvent[] | undefined }) {
  const qc = useQueryClient();
  const stageIndex = task.stages.findIndex((s) => s.key === task.stage_key);
  const sendBackOptions = stageIndex >= 0 ? task.stages.slice(0, stageIndex + 1) : task.stages;
  const [sendBackTo, setSendBackTo] = useState(task.stage_key);
  const skip = skipChoice(task);
  const [advanceTo, setAdvanceTo] = useState("");
  const { findings } = currentRound(timeline);
  const running = task.parallel_reviews ?? [];
  const parallelFailed = findings.filter((f) => f.data.parallel === true && str(f.data.verdict) === "fail");
  const { confirm } = useDialogs();
  /** Before approving: the AI review running alongside is unfinished or found problems. */
  const confirmApprove = async () => {
    if (running.length > 0) {
      const name = running[0].gate?.name ?? "The AI review";
      if (!(await confirm({ title: `${name} is still running`, body: <p>Approving now cancels it, and its findings will not be recorded.</p>, confirmLabel: "Approve anyway" })))
        throw new SubmitCancelled();
    } else if (parallelFailed.length > 0) {
      if (!(await confirm({ title: "The AI review found problems", body: <p>Its findings are listed in the review panel. Approve anyway?</p>, confirmLabel: "Approve anyway" })))
        throw new SubmitCancelled();
    }
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: qk.task(task.id) });
    qc.invalidateQueries({ queryKey: qk.tasks(task.project_id) });
    qc.invalidateQueries({ queryKey: qk.timeline(task.id) });
  };
  const review = useMutation({
    meta: { inlineError: true },
    mutationFn: (v: { decision: "approve" | "reject"; body: string; ids: string[] }) =>
      api.reviewTask(task.id, {
        decision: v.decision,
        body: v.body,
        attachment_ids: v.ids,
        ...(v.decision === "reject" ? { send_back_to: sendBackTo } : {}),
        ...(v.decision === "approve" && advanceTo ? { advance_to: advanceTo } : {}),
      }),
    onSettled: invalidate,
  });

  return (
    <Composer
      placeholder="Feedback for the agent. Paste annotated screenshots, drop logs or specs."
      rows={4}
      hint="Review requested: approve or reject. Comment leaves a note without deciding."
      actions={[
        {
          label: "Comment",
          onSubmit: async (body, ids) => {
            await api.comment(task.id, body, ids);
            invalidate();
          },
        },
        {
          label: "Reject",
          variant: "danger",
          allowEmpty: true,
          onSubmit: (body, ids) => review.mutateAsync({ decision: "reject", body, ids }),
        },
        {
          label: "Approve",
          variant: "primary",
          allowEmpty: true,
          onSubmit: async (body, ids) => {
            await confirmApprove();
            return review.mutateAsync({ decision: "approve", body, ids });
          },
        },
      ]}
    >
      {skip.targets.length > 0 && (
        <label className="send-back">
          <span className="muted small">Approve continues to</span>
          <select className="input input-inline" value={advanceTo} onChange={(e) => setAdvanceTo(e.target.value)}>
            <option value="">{skip.continueLabel}</option>
            {skip.targets.map((s) => (
              <option key={s.key} value={s.key}>
                Skip to {s.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="send-back">
        <span className="muted small">Reject sends back to</span>
        <select className="input input-inline" value={sendBackTo} onChange={(e) => setSendBackTo(e.target.value)}>
          {sendBackOptions.map((s) => (
            <option key={s.key} value={s.key}>
              {s.name}
              {s.key === task.stage_key ? " (current)" : ""}
            </option>
          ))}
        </select>
      </label>
    </Composer>
  );
}

/** A review section that starts collapsed; its content only mounts (and loads) when opened. */
function ReviewToggle({ title, summary, children }: { title: string; summary?: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="review-block review-toggle">
      <button type="button" className="review-toggle-head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Icon name={open ? "chevronDown" : "chevronRight"} size={14} />
        <h4>{title}</h4>
        {summary && <span className="review-toggle-summary">{summary}</span>}
      </button>
      {open && <div className="review-toggle-body">{children}</div>}
    </div>
  );
}

/** Verdict counts shown on the collapsed findings toggle, e.g. "1 pass · 1 fail". */
function VerdictSummary({ findings }: { findings: TimelineEvent[] }) {
  const passed = findings.filter((f) => str(f.data.verdict) === "pass").length;
  const failed = findings.filter((f) => str(f.data.verdict) === "fail").length;
  return (
    <>
      {passed > 0 && <Pill color="green">{passed} pass</Pill>}
      {failed > 0 && <Pill color="red">{failed} fail</Pill>}
      {passed + failed === 0 && <span>{findings.length} result{findings.length === 1 ? "" : "s"}</span>}
    </>
  );
}

/** Last line of `git diff --stat`, e.g. "2 files changed, 6 insertions(+)". */
function diffSummary(stat: string | undefined): string {
  const last = stat?.trim().split("\n").pop()?.trim();
  return last && /changed/.test(last) ? last : "";
}

/** Opens the rich diff view of a task's change in its own window. */
export function OpenRichDiff({ projectId, taskId }: { projectId: string; taskId: string }) {
  return (
    <button
      className="btn btn-sm"
      onClick={() => window.open(`/p/${projectId}/tasks/${taskId}/rich-diff`, `rich-diff-${taskId}`)}
      title="Traceability and database schema changes, in a new window"
    >
      <Icon name="external" size={13} /> Open rich diff
    </button>
  );
}

export function TaskDiffSection({ taskId }: { taskId: string }) {
  const { data, isLoading, error } = useQuery({ queryKey: qk.diff(taskId), queryFn: () => api.diff(taskId) });
  if (isLoading) return <Spinner label="Loading diff…" />;
  if (error) return <ErrorBox error={error} />;
  if (!data) return <p className="muted">No diff captured yet.</p>;
  return (
    <div>
      <div className="muted small diff-captured">
        Captured <RelativeTime iso={data.captured_at} />
      </div>
      {data.stat && <pre className="diff-stat">{data.stat}</pre>}
      <DiffView diff={data.diff} />
    </div>
  );
}
